import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Chip,
  Tooltip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Inbox as InboxIcon,
  WifiTethering as WifiIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';
import EmailManagerDialog from './EmailManagerDialog';

const STORAGE_KEY_V1 = 'admin_custom_email_server_v1';
const STORAGE_KEY_V2 = 'admin_custom_email_servers_v2';

type TlsMode = 'none' | 'tls' | 'ssl';

type SmtpConfig = {
  host: string;
  port: number;
  tls: TlsMode;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
};

type ImapConfig = {
  host: string;
  port: number;
  tls: TlsMode;
  username: string;
  password: string;
  mailbox: string;
};

type PopConfig = {
  host: string;
  port: number;
  tls: TlsMode;
  username: string;
  password: string;
};

type State = {
  smtp: SmtpConfig;
  imap: ImapConfig;
  pop: PopConfig;
};

type Profile = {
  id: string;
  name: string;
  state: State;
};

type MultiState = {
  selectedId: string;
  profiles: Profile[];
};

const defaultState: State = {
  smtp: {
    host: '',
    port: 587,
    tls: 'tls',
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
  },
  imap: {
    host: '',
    port: 993,
    tls: 'ssl',
    username: '',
    password: '',
    mailbox: 'INBOX',
  },
  pop: {
    host: '',
    port: 995,
    tls: 'ssl',
    username: '',
    password: '',
  },
};

const newId = () => {
  try {
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // ignore
  }
  return 'id_' + Math.random().toString(16).slice(2) + '_' + Date.now().toString(16);
};

const createProfile = (name: string, state?: Partial<State>): Profile => {
  return {
    id: newId(),
    name: (name || '').trim() || 'Email Server',
    state: {
      smtp: { ...defaultState.smtp, ...(state?.smtp || {}) },
      imap: { ...defaultState.imap, ...(state?.imap || {}) },
      pop: { ...defaultState.pop, ...(state?.pop || {}) },
    },
  };
};

const safeJsonParse = (s: string | null) => {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const friendlyError = (msg: string) => {
  const m = (msg || '').toLowerCase();
  if (m.includes('you entered an imap server in the smtp')) {
    return 'You used IMAP settings in the SMTP (Send) box. Please use your SMTP host (usually starts with "smtp.") and port 587 (TLS) or 465 (SSL).';
  }
  if (m.includes('you entered a pop server in the smtp')) {
    return 'You used POP settings in the SMTP (Send) box. POP is only for receiving emails. Please use SMTP host/port for sending.';
  }
  if (m.includes('secure connection') && m.includes('enable openssl')) {
    return 'Your server is missing the secure mail feature (OpenSSL). Ask your host to enable OpenSSL in PHP, or switch to a PHP setup that has it.';
  }
  if (m.includes('login failed')) {
    return 'Login failed. Double-check the email/username and password. Some providers require an “App Password”.';
  }
  if (m.includes('could not reach the smtp server')) {
    return 'Could not reach the mail server. Double-check the server address (host) and port.';
  }
  return msg;
};

const showFriendlyError = (err: unknown, fallback: string) => {
  const raw = (err as any)?.message || (err as any)?.toString?.() || fallback;
  const friendly = friendlyError(typeof raw === 'string' ? raw : fallback);
  toast.error(friendly);
  if (typeof raw === 'string' && raw.includes('\n')) {
    toast((t) => (
      <span>
        {raw}
      </span>
    ), { duration: 9000 });
  }
};

const looksLikePopHost = (host: string) => /^\s*pop\./i.test(host || '');
const looksLikeImapHost = (host: string) => /^\s*imap\./i.test(host || '');
const looksLikeSmtpHost = (host: string) => /^\s*smtp\./i.test(host || '');

const CustomEmailServerTab: React.FC = () => {
  const [multi, setMulti] = React.useState<MultiState>(() => {
    const savedV2 = safeJsonParse(localStorage.getItem(STORAGE_KEY_V2));
    if (savedV2 && typeof savedV2 === 'object' && Array.isArray(savedV2.profiles) && typeof savedV2.selectedId === 'string') {
      const profiles: Profile[] = (savedV2.profiles as any[]).map((p) => ({
        id: typeof p?.id === 'string' ? p.id : newId(),
        name: typeof p?.name === 'string' && p.name.trim() ? p.name : 'Email Server',
        state: {
          smtp: { ...defaultState.smtp, ...(p?.state?.smtp || {}) },
          imap: { ...defaultState.imap, ...(p?.state?.imap || {}) },
          pop: { ...defaultState.pop, ...(p?.state?.pop || {}) },
        },
      }));
      const selectedId = profiles.some((p) => p.id === savedV2.selectedId) ? savedV2.selectedId : (profiles[0]?.id || '');
      if (profiles.length > 0 && selectedId) return { profiles, selectedId };
    }

    const savedV1 = safeJsonParse(localStorage.getItem(STORAGE_KEY_V1));
    if (savedV1 && typeof savedV1 === 'object') {
      const p = createProfile('Email Server 1', savedV1 as any);
      return { profiles: [p], selectedId: p.id };
    }

    const p = createProfile('Email Server 1');
    return { profiles: [p], selectedId: p.id };
  });

  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState('');
  const [emailManagerOpen, setEmailManagerOpen] = React.useState(false);

  const [sendForm, setSendForm] = React.useState({
    to: '',
    subject: 'Test Email',
    text: 'Hello from your Custom Email Server!',
    html: '',
  });

  const [imapFolderList, setImapFolderList] = React.useState<string[]>([]);
  const [imapMessages, setImapMessages] = React.useState<any[]>([]);

  const selectedProfile = React.useMemo(() => {
    return multi.profiles.find((p) => p.id === multi.selectedId) || multi.profiles[0];
  }, [multi.profiles, multi.selectedId]);

  const selectedState: State = selectedProfile?.state || defaultState;

  const updateSelectedState = (patch: Partial<State>) => {
    setMulti((prev) => {
      const profiles = prev.profiles.map((p) => {
        if (p.id !== prev.selectedId) return p;
        return {
          ...p,
          state: {
            smtp: { ...p.state.smtp, ...(patch.smtp || {}) },
            imap: { ...p.state.imap, ...(patch.imap || {}) },
            pop: { ...p.state.pop, ...(patch.pop || {}) },
          },
        };
      });
      return { ...prev, profiles };
    });
  };

  const setSelectedId = (id: string) => {
    setImapFolderList([]);
    setImapMessages([]);
    setMulti((p) => ({ ...p, selectedId: id }));
  };

  const callMailserversApi = async (action: 'save' | 'load', data?: any) => {
    let token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/mailservers.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(action === 'save' ? { data } : {}) }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      const reason = (json && (json.error || json.reason || json.detail)) ? (json.error || json.reason || json.detail) : `HTTP ${res.status}`;
      throw new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
    }
    return json;
  };

  const openEmailManager = () => {
    setEmailManagerOpen(true);
  };

  const saveToServer = async () => {
    try {
      setSaving(true);
      await callMailserversApi('save', multi);
      toast.success('Saved');
    } catch (e: any) {
      showFriendlyError(e, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const loadFromServer = async () => {
    try {
      setLoading(true);
      const json = await callMailserversApi('load');
      const data = json?.data;
      if (!data) {
        toast('No saved mailservers.json found yet. Click Save to create it.');
        return;
      }
      if (typeof data !== 'object' || !Array.isArray((data as any).profiles) || typeof (data as any).selectedId !== 'string') {
        toast.error('Saved file exists, but it is not in the expected format.');
        return;
      }
      setMulti(data as MultiState);
      toast.success('Loaded');
    } catch (e: any) {
      showFriendlyError(e, 'Load failed');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_V2, JSON.stringify(multi));
    } catch {
      // ignore
    }
  }, [multi]);

  React.useEffect(() => {
    try {
      const savedV2 = safeJsonParse(localStorage.getItem(STORAGE_KEY_V2));
      if (savedV2 && typeof savedV2 === 'object' && Array.isArray((savedV2 as any).profiles)) {
        return;
      }
    } catch {
      // ignore
    }
    loadFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addProfile = () => {
    const p = createProfile(`Email Server ${multi.profiles.length + 1}`);
    setMulti((prev) => ({ ...prev, profiles: [p, ...prev.profiles], selectedId: p.id }));
    toast.success('New email server added');
  };

  const duplicateProfile = (id: string) => {
    const src = multi.profiles.find((p) => p.id === id);
    if (!src) return;
    const p = createProfile(`${src.name} (copy)`, src.state);
    setMulti((prev) => ({ ...prev, profiles: [p, ...prev.profiles], selectedId: p.id }));
    toast.success('Copied');
  };

  const deleteProfile = (id: string) => {
    if (multi.profiles.length <= 1) {
      toast.error('You need at least one email server');
      return;
    }
    setMulti((prev) => {
      const nextProfiles = prev.profiles.filter((p) => p.id !== id);
      const nextSelected = prev.selectedId === id ? (nextProfiles[0]?.id || '') : prev.selectedId;
      return { profiles: nextProfiles, selectedId: nextSelected };
    });
    toast.success('Removed');
  };

  const openRename = () => {
    setRenameValue(selectedProfile?.name || '');
    setRenameOpen(true);
  };

  const saveRename = () => {
    const name = renameValue.trim() || 'Email Server';
    setMulti((prev) => ({
      ...prev,
      profiles: prev.profiles.map((p) => (p.id === prev.selectedId ? { ...p, name } : p)),
    }));
    setRenameOpen(false);
    toast.success('Renamed');
  };

  const callApi = async (action: string, payload: any) => {
    let token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/custom-email-server.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...payload }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      const base = (json && (json.error || json.reason)) ? (json.error || json.reason) : `HTTP ${res.status}`;
      const detail = json?.detail;
      if (detail && typeof detail === 'object' && (detail.what_happened || detail.how_to_fix || detail.php_runtime)) {
        const lines: string[] = [];
        if (typeof base === 'string') lines.push(base);
        if (typeof detail.what_happened === 'string' && detail.what_happened.trim()) {
          lines.push('');
          lines.push(detail.what_happened);
        }
        if (Array.isArray(detail.how_to_fix) && detail.how_to_fix.length) {
          lines.push('');
          lines.push('Fix steps:');
          for (const s of detail.how_to_fix) {
            if (typeof s === 'string' && s.trim()) lines.push(`- ${s}`);
          }
        }
        if (detail.php_runtime && typeof detail.php_runtime === 'object') {
          const ini = (detail.php_runtime.php_ini_loaded_file || '').toString().trim();
          const bin = (detail.php_runtime.php_binary || '').toString().trim();
          if (ini || bin) {
            lines.push('');
            lines.push('Server PHP info:');
            if (bin) lines.push(`- PHP: ${bin}`);
            if (ini) lines.push(`- php.ini: ${ini}`);
          }
        }
        if (typeof detail.quick_check === 'string' && detail.quick_check.trim()) {
          lines.push('');
          lines.push(detail.quick_check);
        }
        throw new Error(lines.filter(Boolean).join('\n'));
      }

      const reason = (json && (json.error || json.reason || json.detail)) ? (json.error || json.reason || json.detail) : `HTTP ${res.status}`;
      throw new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
    }
    return json;
  };

  const smtpTest = async () => {
    const h = (selectedState.smtp.host || '').trim();
    if (looksLikePopHost(h) || looksLikeImapHost(h) || [110, 143, 993, 995].includes(Number(selectedState.smtp.port))) {
      toast.error('SMTP (Send) uses SMTP settings. Use an SMTP host (usually starts with “smtp.”) and port 587 (TLS) or 465 (SSL).');
      return;
    }
    try {
      setBusy('smtp_test');
      await callApi('smtp_test', { smtp: selectedState.smtp });
      toast.success('SMTP connection OK');
    } catch (e: any) {
      showFriendlyError(e, 'SMTP test failed');
    } finally {
      setBusy(null);
    }
  };

  const smtpSend = async () => {
    if (!sendForm.to.trim()) {
      toast.error('Recipient is required');
      return;
    }
    const h = (selectedState.smtp.host || '').trim();
    if (looksLikePopHost(h) || looksLikeImapHost(h) || [110, 143, 993, 995].includes(Number(selectedState.smtp.port))) {
      toast.error('SMTP (Send) uses SMTP settings. Use an SMTP host (usually starts with “smtp.”) and port 587 (TLS) or 465 (SSL).');
      return;
    }
    try {
      setBusy('smtp_send');
      await callApi('smtp_send', {
        smtp: selectedState.smtp,
        message: {
          to: sendForm.to.trim(),
          subject: sendForm.subject || '(no subject)',
          text: sendForm.text || '',
          html: sendForm.html || '',
        },
      });
      toast.success('Email sent');
    } catch (e: any) {
      showFriendlyError(e, 'Send failed');
    } finally {
      setBusy(null);
    }
  };

  const imapTest = async () => {
    try {
      setBusy('imap_test');
      await callApi('imap_test', { imap: selectedState.imap });
      toast.success('IMAP connection OK');
    } catch (e: any) {
      showFriendlyError(e, 'IMAP test failed');
    } finally {
      setBusy(null);
    }
  };

  const imapListFolders = async () => {
    try {
      setBusy('imap_list_folders');
      const json = await callApi('imap_list_folders', { imap: selectedState.imap });
      setImapFolderList(Array.isArray(json?.folders) ? json.folders : []);
      toast.success('Folders loaded');
    } catch (e: any) {
      showFriendlyError(e, 'Failed to list folders');
    } finally {
      setBusy(null);
    }
  };

  const imapFetch = async () => {
    try {
      setBusy('imap_fetch');
      const json = await callApi('imap_fetch', { imap: selectedState.imap, limit: 20 });
      setImapMessages(Array.isArray(json?.messages) ? json.messages : []);
      toast.success('Inbox fetched');
    } catch (e: any) {
      showFriendlyError(e, 'Failed to fetch inbox');
    } finally {
      setBusy(null);
    }
  };

  const popTest = async () => {
    const h = (selectedState.pop.host || '').trim();
    if (looksLikeSmtpHost(h) || looksLikeImapHost(h) || [25, 465, 587, 143, 993].includes(Number(selectedState.pop.port))) {
      toast.error('POP is for receiving email. Use a POP host (often starts with “pop.”) and port 995 (SSL) or 110.');
      return;
    }
    try {
      setBusy('pop_test');
      await callApi('pop_test', { pop: selectedState.pop });
      toast.success('POP connection OK');
    } catch (e: any) {
      showFriendlyError(e, 'POP test failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Box>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            backgroundImage: 'radial-gradient(1200px circle at 0% 0%, rgba(59,130,246,0.08), transparent 40%), radial-gradient(1200px circle at 100% 0%, rgba(16,185,129,0.08), transparent 40%)',
          }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" />
              Custom Email Server
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Connect to any provider using SMTP (send) and IMAP (receive). Click Save to store your settings on the server.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              onClick={saveToServer}
              disabled={saving || loading}
              startIcon={<SaveIcon />}
              sx={{ borderRadius: 999, px: 3, py: 1.1, fontWeight: 700 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Typography variant="h6" fontWeight={800}>
                  Email Servers
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={addProfile} sx={{ borderRadius: 999, fontWeight: 800 }}>
                  Add
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Pick an account on the left, edit settings on the right.
              </Typography>
              <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {multi.profiles.map((p) => {
                  const active = p.id === multi.selectedId;
                  return (
                    <Paper key={p.id} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: active ? 'primary.main' : 'divider', overflow: 'hidden' }}>
                      <ListItemButton selected={active} onClick={() => setSelectedId(p.id)} sx={{ py: 1.1 }}>
                        <ListItemText
                          primary={p.name}
                          secondary={
                            <span>
                              {(p.state.smtp.fromEmail || p.state.smtp.username || '').trim() || 'Not configured yet'}
                            </span>
                          }
                        />
                        <Tooltip title="Duplicate">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); duplicateProfile(p.id); }}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteProfile(p.id); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemButton>
                    </Paper>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={900}>
                    {selectedProfile?.name || 'Email Server'}
                  </Typography>
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={openRename}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Chip label="Saved to mailservers.json" color="default" variant="outlined" />
              </Box>
            </Paper>

            <Box sx={{ height: 12 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WifiIcon fontSize="small" /> SMTP (Send)
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={smtpTest}
                      disabled={busy !== null}
                      startIcon={<RefreshIcon />}
                      sx={{ borderRadius: 999 }}
                    >
                      Test
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                      <TextField
                        label="SMTP Host"
                        value={selectedState.smtp.host}
                        onChange={(e) => updateSelectedState({ smtp: { host: e.target.value } as any })}
                        fullWidth
                        placeholder="smtp.yourdomain.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField
                        label="Port"
                        value={selectedState.smtp.port}
                        onChange={(e) => updateSelectedState({ smtp: { port: Number(e.target.value || 0) } as any })}
                        fullWidth
                        type="number"
                        inputProps={{ min: 1, max: 65535 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>TLS</InputLabel>
                        <Select
                          label="TLS"
                          value={selectedState.smtp.tls}
                          onChange={(e) => updateSelectedState({ smtp: { tls: e.target.value as TlsMode } as any })}
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="tls">STARTTLS (recommended)</MenuItem>
                          <MenuItem value="ssl">SSL</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Username"
                        value={selectedState.smtp.username}
                        onChange={(e) => updateSelectedState({ smtp: { username: e.target.value } as any })}
                        fullWidth
                        placeholder="your@email.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Password"
                        value={selectedState.smtp.password}
                        onChange={(e) => updateSelectedState({ smtp: { password: e.target.value } as any })}
                        fullWidth
                        type="password"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="From Email"
                        value={selectedState.smtp.fromEmail}
                        onChange={(e) => updateSelectedState({ smtp: { fromEmail: e.target.value } as any })}
                        fullWidth
                        placeholder="no-reply@yourdomain.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="From Name"
                        value={selectedState.smtp.fromName}
                        onChange={(e) => updateSelectedState({ smtp: { fromName: e.target.value } as any })}
                        fullWidth
                        placeholder="Your Brand"
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SendIcon fontSize="small" /> Send Test Email
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="To"
                        value={sendForm.to}
                        onChange={(e) => setSendForm((p) => ({ ...p, to: e.target.value }))}
                        fullWidth
                        placeholder="recipient@example.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Subject"
                        value={sendForm.subject}
                        onChange={(e) => setSendForm((p) => ({ ...p, subject: e.target.value }))}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Text Body"
                        value={sendForm.text}
                        onChange={(e) => setSendForm((p) => ({ ...p, text: e.target.value }))}
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Collapse in={true}>
                        <TextField
                          label="HTML Body (optional)"
                          value={sendForm.html}
                          onChange={(e) => setSendForm((p) => ({ ...p, html: e.target.value }))}
                          fullWidth
                          multiline
                          minRows={3}
                          placeholder="<h1>Hello</h1>"
                        />
                      </Collapse>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        onClick={smtpSend}
                        disabled={busy !== null}
                        sx={{ borderRadius: 999, px: 3, py: 1.1, fontWeight: 800 }}
                      >
                        {busy === 'smtp_send' ? 'Sending…' : 'Send'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InboxIcon fontSize="small" /> IMAP (Receive)
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={imapTest}
                      disabled={busy !== null}
                      startIcon={busy === 'imap_test' ? <CircularProgress size={18} thickness={5} /> : <RefreshIcon />}
                      sx={{ borderRadius: 999 }}
                    >
                      {busy === 'imap_test' ? 'Testing…' : 'Test'}
                    </Button>
                  </Box>

                  {(busy === 'imap_list_folders' || busy === 'imap_fetch') && (
                    <LinearProgress sx={{ mb: 2, borderRadius: 999 }} />
                  )}

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                      <TextField
                        label="IMAP Host"
                        value={selectedState.imap.host}
                        onChange={(e) => updateSelectedState({ imap: { host: e.target.value } as any })}
                        fullWidth
                        placeholder="imap.yourdomain.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField
                        label="Port"
                        value={selectedState.imap.port}
                        onChange={(e) => updateSelectedState({ imap: { port: Number(e.target.value || 0) } as any })}
                        fullWidth
                        type="number"
                        inputProps={{ min: 1, max: 65535 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>TLS</InputLabel>
                        <Select
                          label="TLS"
                          value={selectedState.imap.tls}
                          onChange={(e) => updateSelectedState({ imap: { tls: e.target.value as TlsMode } as any })}
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="tls">TLS</MenuItem>
                          <MenuItem value="ssl">SSL (recommended)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Username"
                        value={selectedState.imap.username}
                        onChange={(e) => updateSelectedState({ imap: { username: e.target.value } as any })}
                        fullWidth
                        placeholder="your@email.com"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Password"
                        value={selectedState.imap.password}
                        onChange={(e) => updateSelectedState({ imap: { password: e.target.value } as any })}
                        fullWidth
                        type="password"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Mailbox"
                        value={selectedState.imap.mailbox}
                        onChange={(e) => updateSelectedState({ imap: { mailbox: e.target.value } as any })}
                        fullWidth
                        placeholder="INBOX"
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          onClick={imapListFolders}
                          disabled={busy !== null}
                          startIcon={busy === 'imap_list_folders' ? <CircularProgress size={18} thickness={5} /> : <RefreshIcon />}
                          sx={{ borderRadius: 999, fontWeight: 800 }}
                        >
                          {busy === 'imap_list_folders' ? 'Loading…' : 'List Folders'}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={imapFetch}
                          disabled={busy !== null}
                          startIcon={busy === 'imap_fetch' ? <CircularProgress size={18} thickness={5} /> : <InboxIcon />}
                          sx={{ borderRadius: 999, fontWeight: 800 }}
                        >
                          {busy === 'imap_fetch' ? 'Fetching…' : 'Fetch Inbox'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={openEmailManager}
                          disabled={busy !== null}
                          sx={{ borderRadius: 999, fontWeight: 800 }}
                        >
                          Open Email Manager
                        </Button>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Collapse in={imapFolderList.length > 0}>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {imapFolderList.slice(0, 30).map((f) => (
                            <Chip key={f} label={f} size="small" />
                          ))}
                          {imapFolderList.length > 30 && (
                            <Tooltip title={`${imapFolderList.length} total folders`}>
                              <Chip label={`+${imapFolderList.length - 30} more`} size="small" variant="outlined" />
                            </Tooltip>
                          )}
                        </Box>
                      </Collapse>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Collapse in={imapMessages.length > 0}>
                        <Paper elevation={0} sx={{ p: 1.5, mt: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 360, overflow: 'auto' }}>
                          {imapMessages.map((m, idx) => (
                            <Box key={idx} sx={{ py: 1, borderBottom: idx === imapMessages.length - 1 ? 'none' : '1px solid', borderColor: 'divider' }}>
                              <Typography fontWeight={800} variant="body2">{m.subject || '(no subject)'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                From: {m.from || '-'} | Date: {m.date || '-'}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Collapse>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
    </Paper>
      </Box>

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename email server</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRename}>Save</Button>
        </DialogActions>
      </Dialog>

      <EmailManagerDialog
        open={emailManagerOpen}
        onClose={() => setEmailManagerOpen(false)}
        imap={selectedState.imap}
        callApi={callApi}
      />
    </>
  );
};

export default CustomEmailServerTab;
